import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Button, ButtonDirective, ButtonIcon } from 'primeng/button';
import { QuoteRevision } from '../../models/quote-revision.model';
import { Quote } from '../../models/quote.model';
import { QuoteService } from '../quote.service';
import { Card } from 'primeng/card';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { Divider } from 'primeng/divider';
import { Droppable } from 'primeng/dragdrop';
import { Popover } from 'primeng/popover';
import { Tag } from 'primeng/tag';
import { User } from '../../models/user.model';
import { TooltipModule } from 'primeng/tooltip';
import { Settings } from '../../models/settings.model';
import { QuotesService } from '../quotes.service';
import { UserService } from '../services/user.service';
import { SettingsService } from '../settings-page/settings-service/settings.service';
import { QuoteHistoryComponent } from './quote-history/quote-history.component';
import { QuoteInfoComponent } from './quote-info/quote-info.component';
import { QuoteRevisionsComponent } from './quote-revisions/quote-revisions.component';
import { ShippingPackagingEditComponent } from './shipping-packaging-edit/shipping-packaging-edit.component';
import { CurrencyExchangeEditComponent } from './currency-exchange-edit/currency-exchange-edit.component';
import { TradeStudyLayoutDialogComponent } from './trade-study-layout-dialog/trade-study-layout-dialog.component';
import { TradeStudyChartsDialogComponent } from './trade-study-charts-dialog/trade-study-charts-dialog.component';
import { CheckboxModule } from 'primeng/checkbox';
import { QuoteRevisionItemComponent } from './quote-revision-item/quote-revision-item.component';
import { QuoteRevisionItemAddComponent } from './quote-revision-item/quote-revision-item-add/quote-revision-item-add.component';
import { QuoteRevisionItemBulkEditDialogComponent } from './quote-revision-item/quote-revision-item-bulk-edit-dialog/quote-revision-item-bulk-edit-dialog.component';
import { QuoteRevisionItemBulkDeleteDialogComponent } from './quote-revision-item/quote-revision-item-bulk-delete-dialog/quote-revision-item-bulk-delete-dialog.component';
import { QuoteRevisionItemService } from '../services/quote-revision-item.service';
import { QuoteRevisionItem } from '../../models/quote-revision-item.model';
interface QuotePageData {
  quote: Quote | undefined;
  revisions: QuoteRevision[] | undefined;
  revision: QuoteRevision | undefined;
}

@Component({
  selector: 'app-quote-page',
  imports: [
    Button,
    AsyncPipe,
    Card,
    ReactiveFormsModule,
    FormsModule,
    Divider,
    ButtonDirective,
    QuoteInfoComponent,
    Droppable,
    Tag,
    QuoteHistoryComponent,
    QuoteRevisionsComponent,
    CurrencyPipe,
    DatePipe,
    ShippingPackagingEditComponent,
    CurrencyExchangeEditComponent,
    SharedConfirmDialogComponent,
    Popover,
    ButtonIcon,
    TooltipModule,
    TradeStudyLayoutDialogComponent,
    TradeStudyChartsDialogComponent,
    CheckboxModule,
    QuoteRevisionItemComponent,
    QuoteRevisionItemAddComponent,
    QuoteRevisionItemBulkEditDialogComponent,
    QuoteRevisionItemBulkDeleteDialogComponent,
  ],
  templateUrl: './quote-page.component.html',
  styleUrl: './quote-page.component.scss',
  providers: [QuoteService, QuoteRevisionItemService, ConfirmationService],
})
export class QuotePageComponent implements OnInit, OnDestroy {
  quotesService = inject(QuotesService);
  userService = inject(UserService);
  quoteService = inject(QuoteService);
  quoteRevisionItemService = inject(QuoteRevisionItemService);
  settingsService = inject(SettingsService);
  activatedRoute = inject(ActivatedRoute);
  confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);

  @ViewChild(ShippingPackagingEditComponent)
  shippingPackagingEdit?: ShippingPackagingEditComponent;

  @ViewChild(CurrencyExchangeEditComponent)
  currencyExchangeEdit?: CurrencyExchangeEditComponent;

  private revisionItemListScrollObserver?: ResizeObserver;
  private quoteRevisionItemsSubscription?: Subscription;
  private settingsSubscription?: Subscription;

  revisionItemAddDialogVisible = false;
  revisionItemAddParentPersistentId: string | null = null;

  layoutDialogVisible = false;
  chartsDialogVisible = false;
  historyDrawerVisible = false;
  revisionItemBulkEditDialogVisible = false;
  revisionItemBulkDeleteDialogVisible = false;
  selectedRevisionItemPersistentIds = new Set<string>();

  quoteId: string | null = null;
  currentUser: User | undefined;
  settings: Settings | undefined;

  pageData$: Observable<QuotePageData> = combineLatest([
    this.quoteService.quote$,
    this.quoteService.revisions$,
    this.quoteService.revision$,
  ]).pipe(
    map(([quote, revisions, revision]) => {
      return {
        quote,
        revisions,
        revision,
      };
    }),
  );

  async ngOnInit(): Promise<void> {
    this.quoteRevisionItemsSubscription = combineLatest([
      this.quoteService.quote$,
      this.quoteService.revision$,
    ])
      .pipe(
        distinctUntilChanged(
          ([previousQuote, previousRevision], [quote, revision]) =>
            previousQuote?.id === quote?.id &&
            previousRevision?.number === revision?.number,
        ),
      )
      .subscribe(([quote, revision]) => {
        if (quote && revision) {
          void this.quoteRevisionItemService
            .getQuoteRevisionItems(quote.id, revision.number)
            .catch(() => undefined);
        }
      });

    const paramMap: ParamMap = await firstValueFrom(
      this.activatedRoute.paramMap,
    );
    this.quoteId = paramMap.get('quoteId')!;

    const revisionNumber = +paramMap.get('revisionNumber')!;

    // Load user and settings for approval flow
    this.currentUser = (await this.userService.loadCurrentUser()) ?? undefined;
    // settings$ starts as null until the first fetch finishes, so follow it
    // rather than taking a one-time snapshot.
    this.settingsSubscription = this.settingsService.settings$.subscribe(
      (settings) => {
        this.settings = settings ?? undefined;
      },
    );

    await this.quoteService.getQuote(this.quoteId);
    await this.quoteService.getRevisions();
    await this.quoteService.loadRevision(revisionNumber);
  }

  ngOnDestroy(): void {
    this.revisionItemListScrollObserver?.disconnect();
    this.quoteRevisionItemsSubscription?.unsubscribe();
    this.settingsSubscription?.unsubscribe();
  }

  @ViewChild('revisionItemListScroll')
  set revisionItemListScrollRef(ref: ElementRef<HTMLElement> | undefined) {
    this.revisionItemListScrollObserver?.disconnect();
    this.revisionItemListScrollObserver = undefined;

    const scrollEl = ref?.nativeElement;
    if (!scrollEl) {
      return;
    }

    this.updateItemListScrollStuck(scrollEl);
    this.revisionItemListScrollObserver = new ResizeObserver(() => {
      this.updateItemListScrollStuck(scrollEl);
    });
    // Observe both the scroll element and the scroll content (the item list rows).
    // The scroll container can change width with the page, and the scroll content can change width
    // from taking up the page width down to preset min widths depending on the number of columns it's showing.
    this.revisionItemListScrollObserver.observe(scrollEl);
    const scrollContent = scrollEl.firstElementChild;
    if (scrollContent) {
      this.revisionItemListScrollObserver.observe(scrollContent);
    }
  }

  onItemListScroll(event: Event): void {
    const scrollEl = event.currentTarget as HTMLElement | null;
    if (scrollEl) {
      this.updateItemListScrollStuck(scrollEl);
    }
  }

  private updateItemListScrollStuck(scrollEl: HTMLElement): void {
    // If there is still more scrolling to be done, add a class reporting that the sticky end is stuck so we can add some styling to it.
    const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
    const isStuck =
      maxScrollLeft > 1 && scrollEl.scrollLeft < maxScrollLeft - 1;
    scrollEl.classList.toggle('item-list-scroll-stuck', isStuck);
  }

  // Approval flow helper methods
  get isApprovalFlowEnabled(): boolean {
    return this.settings?.approval_flow_enabled ?? false;
  }

  get canRequestReview(): boolean {
    const role = this.currentUser?.role;
    return ['superuser', 'admin', 'approver', 'creator'].includes(role ?? '');
  }

  get canApproveOrRequestChanges(): boolean {
    const role = this.currentUser?.role;
    return ['superuser', 'admin', 'approver'].includes(role ?? '');
  }

  get isTradeStudyToolFeaturesEnabled(): boolean {
    return this.settings?.trade_study_tool_features_enabled ?? false;
  }

  get isPDFDrawingUploadEnabled(): boolean {
    return this.settings?.pdf_drawing_upload_enabled ?? false;
  }

  get isConsumablesEnabled(): boolean {
    return this.settings?.consumables_enabled ?? false;
  }

  get itemListMinWidthClass(): string {
    if (this.isTradeStudyToolFeaturesEnabled && this.isConsumablesEnabled) {
      return 'min-w-[1408px] bg-surface-switch-0';
    }
    if (this.isTradeStudyToolFeaturesEnabled) {
      return 'min-w-[1280px] bg-surface-switch-0';
    }
    // Without trade study the layout is already the widest; the extra
    // consumables column shares that width (~96px per column, wider than the
    // trade study layouts) rather than forcing horizontal scroll sooner.
    return 'min-w-[1536px] bg-surface-switch-0';
  }

  get itemListHeaderClass(): string {
    const columns =
      this.isTradeStudyToolFeaturesEnabled && this.isConsumablesEnabled
        ? 'grid-cols-[80px_repeat(8,minmax(0,1fr))_minmax(8.25rem,1fr)_repeat(4,minmax(0,1fr))]'
        : this.isTradeStudyToolFeaturesEnabled
          ? 'grid-cols-[80px_repeat(12,minmax(0,1fr))]'
          : this.isConsumablesEnabled
            ? 'grid-cols-[80px_repeat(9,minmax(0,1fr))_minmax(8.25rem,1fr)_repeat(4,minmax(0,1fr))]'
            : 'grid-cols-[80px_repeat(13,minmax(0,1fr))]';
    return `item-list-column-header grid ${columns} gap-2 border-b border-surface-switch-200 bg-surface-switch-100`;
  }

  async loadQuoteRevision(revisionNumber: number) {
    await this.quoteService.loadRevision(revisionNumber);
  }

  openShippingPackagingDrawer() {
    this.shippingPackagingEdit?.open();
  }

  openCurrencyExchangeDialog() {
    void this.currencyExchangeEdit?.open();
  }

  async onTradeStudyLayoutSaved(): Promise<void> {
    if (!this.quoteId) {
      return;
    }
    await this.quoteService.getQuote(this.quoteId);
    await this.quoteService.getRevisions(true);
    await this.quoteService.loadRevision(
      this.quoteService.quote.current_revision.number,
    );
    await this.quoteRevisionItemService.reload();
  }

  async archiveQuote() {
    await this.quoteService.patchQuote({
      archived_at: new Date().toISOString(),
    });
  }

  async unarchiveQuote() {
    await this.quoteService.patchQuote({ archived_at: null });
  }

  async downloadCADFiles() {
    await this.downloadWithToast(
      () => this.quoteRevisionItemService.downloadQuoteCad(),
      'CAD files',
    );
  }

  async downloadAllDrawingFiles() {
    await this.downloadWithToast(
      () => this.quoteRevisionItemService.downloadAllDrawings(),
      'drawings',
    );
  }

  private async downloadWithToast(
    download: () => Promise<void>,
    noun: string,
  ): Promise<void> {
    try {
      await download();
    } catch (error: unknown) {
      const notFound =
        error instanceof HttpErrorResponse && error.status === 404;
      this.messageService.add({
        severity: notFound ? 'warn' : 'error',
        summary: notFound ? `No ${noun}` : 'Download failed',
        detail: notFound
          ? `There are no ${noun} on this quote to download.`
          : `The ${noun} could not be downloaded.`,
      });
    }
  }

  async downloadAllBlockingUp() {
    await this.quoteRevisionItemService.downloadAllBlockingUp();
  }

  async downloadCSV() {
    await this.quoteRevisionItemService.downloadCSV();
  }

  openLayoutDialog(): void {
    this.layoutDialogVisible = true;
  }

  openChartsDialog(): void {
    this.chartsDialogVisible = true;
  }

  openHistoryDrawer(): void {
    this.historyDrawerVisible = true;
  }

  confirmDeleteQuote() {
    this.confirmationService.confirm({
      header: 'Delete Quote',
      message:
        'Are you sure you want to delete this quote? This action is irreversible.',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        await this.quoteService.deleteQuote();
      },
    });
  }

  isViewingCurrentRevision(
    quote: Quote | undefined,
    revision: QuoteRevision | undefined,
  ): boolean {
    return !!quote && !!revision && quote.current_revision_id === revision.id;
  }

  async openCurrentRevision(
    quote: Quote | undefined,
    revisions: QuoteRevision[] | undefined,
  ): Promise<void> {
    const currentRevision = revisions?.find(
      (revision) => revision.id === quote?.current_revision_id,
    );
    if (currentRevision) {
      await this.quoteService.loadRevision(currentRevision.number);
    }
  }

  get selectedRevisionItemCount(): number {
    return this.selectedRevisionItemPersistentIds.size;
  }

  setRevisionItemSelected(persistentId: string, selected: boolean): void {
    const next = new Set(this.selectedRevisionItemPersistentIds);
    if (selected) {
      next.add(persistentId);
    } else {
      next.delete(persistentId);
    }
    this.selectedRevisionItemPersistentIds = next;
  }

  toggleSelectAllRevisionItems(items: QuoteRevisionItem[]): void {
    const allItems = this.flattenRevisionItems(items);
    const allSelected = allItems.every((item) =>
      this.selectedRevisionItemPersistentIds.has(item.persistent_id),
    );
    this.selectedRevisionItemPersistentIds = allSelected
      ? new Set()
      : new Set(allItems.map((item) => item.persistent_id));
  }

  areAllRevisionItemsSelected(items: QuoteRevisionItem[]): boolean {
    const allItems = this.flattenRevisionItems(items);
    return (
      allItems.length > 0 &&
      allItems.every((item) =>
        this.selectedRevisionItemPersistentIds.has(item.persistent_id),
      )
    );
  }

  openRevisionItemBulkEditDialog(): void {
    if (this.selectedRevisionItemCount === 0) {
      return;
    }
    this.revisionItemBulkEditDialogVisible = true;
  }

  onRevisionItemBulkEditSaved(): void {
    this.selectedRevisionItemPersistentIds = new Set();
  }

  openRevisionItemBulkDeleteDialog(): void {
    if (this.selectedRevisionItemCount === 0) {
      return;
    }
    this.revisionItemBulkDeleteDialogVisible = true;
  }

  onRevisionItemBulkDeleteDeleted(): void {
    this.selectedRevisionItemPersistentIds = new Set();
  }

  getSelectedRevisionItems(
    revisionItems: QuoteRevisionItem[] | undefined,
  ): QuoteRevisionItem[] {
    if (!revisionItems?.length) {
      return [];
    }
    return this.flattenRevisionItems(revisionItems).filter((item) =>
      this.selectedRevisionItemPersistentIds.has(item.persistent_id),
    );
  }

  openRevisionItemAddDialog(parentPersistentId: string | null): void {
    this.revisionItemAddParentPersistentId = parentPersistentId;
    this.revisionItemAddDialogVisible = true;
  }

  dropRevisionItemRootBefore(persistentId: string): void {
    void this.quoteRevisionItemService.moveItem(persistentId, 'before');
  }

  dropRevisionItemRootAtEnd(): void {
    void this.quoteRevisionItemService.moveItem(null, 'end');
  }

  private flattenRevisionItems(
    items: QuoteRevisionItem[],
  ): QuoteRevisionItem[] {
    return items.flatMap((item) => [
      item,
      ...this.flattenRevisionItems(item.children ?? []),
    ]);
  }
}
